/**
 * AppPage — Main translation interface at /app
 * Wires TranslationForm to apiClient and displays results.
 * TRNS-150 sub-task 5: Page wiring + integration smoke test
 */

import { useState, useRef, useCallback } from 'react';
import { TranslationForm } from '../components/TranslationForm/TranslationForm';
import { apiClient } from '../services/apiClient';
import { useAnalytics } from '../hooks/useAnalytics';
import { TranslationResponse } from '../types';
import './AppPage.css';

const SPEED_OPTIONS = [0.5, 0.75, 1, 1.25, 1.5] as const;

export function AppPage() {
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<TranslationResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { trackTranslation } = useAnalytics();

  // Audio / TTS state
  const [ttsLoading, setTtsLoading] = useState(false);
  const [ttsError, setTtsError] = useState<string | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [targetLangForTTS, setTargetLangForTTS] = useState('');

  const handleSpeak = useCallback(async (text: string, language: string) => {
    // Revoke previous blob URL to avoid memory leaks
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
      setAudioUrl(null);
    }
    setTtsError(null);
    setTtsLoading(true);

    try {
      const blob = await apiClient.speak({ text, language, speed: playbackSpeed });
      const url = URL.createObjectURL(blob);
      setAudioUrl(url);

      // Auto-play once loaded
      setTimeout(() => {
        if (audioRef.current) {
          audioRef.current.playbackRate = playbackSpeed;
          audioRef.current.play().catch(() => {
            // Browser may block autoplay — user can click play manually
          });
        }
      }, 0);
    } catch (err) {
      setTtsError(err instanceof Error ? err.message : 'Text-to-speech failed.');
    } finally {
      setTtsLoading(false);
    }
  }, [audioUrl, playbackSpeed]);

  const handleTranslate = async (params: {
    text: string;
    sourceLang: string;
    targetLang: string;
    context: string;
    genderPreferences: { sourceGender?: string; targetGender?: string };
    mode?: string;
  }) => {
    setIsLoading(true);
    setError(null);
    setResult(null);
    // Clear previous audio state on new translation
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
      setAudioUrl(null);
    }
    setTtsError(null);
    setTargetLangForTTS(params.targetLang);

    try {
      const response = await apiClient.translate({
        source: params.text,
        sourceLang: params.sourceLang,
        targetLang: params.targetLang,
        context: params.context as 'formal' | 'casual' | 'business' | 'academic' | 'creative',
        genderPreferences: {
          sourceGender: (params.genderPreferences.sourceGender as 'masculine' | 'feminine' | 'neutral' | 'inclusive') || undefined,
          targetGender: (params.genderPreferences.targetGender as 'masculine' | 'feminine' | 'neutral' | 'inclusive') || undefined,
        },
        mode: params.mode,
      });

      setResult(response);

      // Track successful translation in GA4
      trackTranslation(
        params.sourceLang,
        params.targetLang,
        params.mode || 'standard',
        response.latency
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Translation failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="app-page">
      <header className="app-header">
        <img
          src="/Gemini_Generated_logo_v2_full.png"
          alt="Translayshun"
          className="app-logo"
        />
      </header>

      <main className="app-main">
        <TranslationForm
          onTranslate={handleTranslate}
          isLoading={isLoading}
        />

        {/* Error display */}
        {error && (
          <div className="app-error">
            <p>{error}</p>
          </div>
        )}

        {/* Translation result */}
        {result && (
          <div className="app-result">
            <div className="result-translation">
              <label className="result-label">Translation</label>
              <p className="result-text">{result.translation}</p>
            </div>

            <div className="result-meta">
              <span className="meta-item">
                Confidence: {(result.confidence * 100).toFixed(1)}%
              </span>
              <span className="meta-item">
                {result.latency}ms
              </span>
              {result.metadata.cached && (
                <span className="meta-item cached">Cached</span>
              )}
            </div>

            {result.alternatives && result.alternatives.length > 0 && (
              <div className="result-alternatives">
                <label className="result-label">Alternatives</label>
                <ul>
                  {result.alternatives.map((alt, i) => (
                    <li key={i}>{alt}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Audio / TTS controls */}
            <div className="result-audio">
              <label className="result-label">Listen</label>

              <div className="audio-controls">
                <button
                  className="audio-btn speak-btn"
                  onClick={() => handleSpeak(result.translation, targetLangForTTS)}
                  disabled={ttsLoading}
                  title="Listen to translation"
                >
                  {ttsLoading ? 'Generating...' : 'Play'}
                </button>

                {/* Speed selector */}
                <div className="speed-control">
                  <span className="speed-label">Speed</span>
                  <div className="speed-buttons">
                    {SPEED_OPTIONS.map((spd) => (
                      <button
                        key={spd}
                        className={`speed-btn ${playbackSpeed === spd ? 'active' : ''}`}
                        onClick={() => {
                          setPlaybackSpeed(spd);
                          if (audioRef.current) {
                            audioRef.current.playbackRate = spd;
                          }
                        }}
                      >
                        {spd}x
                      </button>
                    ))}
                  </div>
                </div>

                {/* Download button (only when audio exists) */}
                {audioUrl && (
                  <a
                    className="audio-btn download-btn"
                    href={audioUrl}
                    download="translation.wav"
                    title="Download audio"
                  >
                    Download
                  </a>
                )}
              </div>

              {/* Hidden audio element */}
              {audioUrl && (
                <audio ref={audioRef} src={audioUrl} controls className="audio-player" />
              )}

              {/* TTS error (non-blocking) */}
              {ttsError && (
                <p className="tts-error">{ttsError}</p>
              )}
            </div>
          </div>
        )}
      </main>

      <footer className="app-footer">
        &copy; {new Date().getFullYear()} Translayshun. All rights reserved.
      </footer>
    </div>
  );
}
